import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { DiJira } from 'react-icons/di';

import {
  fetchUserProjects,
  getJiraAuthUrl,
  jiraIncrementalSync,
  updateUserProjects,
  UserProjectsData,
} from '../../shared/api/integration';

const SYNC_PRESETS = [
  { label: 'Last month', months: 1 },
  { label: 'Last 3 months', months: 3 },
  { label: 'Last 6 months', months: 6 },
  { label: 'Last year', months: 12 },
  { label: 'Last 2 years', months: 24 },
] as const;

const syncFromDate = (months: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

const JiraIntegration: React.FC<{
  isConnected?: boolean;
  onDisconnect: (integrationType: string) => void;
  onUpdate?: () => void;
}> = ({ isConnected, onDisconnect, onUpdate }) => {
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [userProjects, setUserProjects] = useState<UserProjectsData | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isChangingProjects, setIsChangingProjects] = useState(false);
  const [pendingSelected, setPendingSelected] = useState<Set<string>>(new Set());
  const [isSavingProjects, setIsSavingProjects] = useState(false);
  const [syncPreset, setSyncPreset] = useState<number>(12);

  const loadProjects = () => {
    setIsLoadingProjects(true);
    fetchUserProjects()
      .then((d) => {
        setUserProjects(d);
        setPendingSelected(new Set(d.selectedProjectIds));
      })
      .catch(() => {})
      .finally(() => setIsLoadingProjects(false));
  };

  useEffect(() => {
    if (isConnected) loadProjects();
    else setUserProjects(null);
  }, [isConnected]);

  const handleJiraIncrementalSync = async () => {
    setErrorMessage(null);

    if (!isConnected) {
      toast.error('Jira is not connected. Please connect first.');
      return;
    }

    setIsSyncing(true);
    try {
      const data = await jiraIncrementalSync();
      console.log('Jira Incremental Sync initiated:', data);
      toast.success('Sync started.');
    } catch (e) {
      console.error('Jira incremental sync failed:', e);
      setErrorMessage('Failed to start sync.');
      toast.error('Failed to start sync.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleJiraLogin = async () => {
    setErrorMessage(null);
    setIsConnecting(true);

    try {
      if (isConnected) {
        onDisconnect('jira');
        return;
      }

      const redirectUrl = await getJiraAuthUrl();
      if (redirectUrl.url) {
        const popupWidth = 600;
        const popupHeight = 600;
        const left = window.innerWidth / 2 - popupWidth / 2;
        const top = window.innerHeight / 2 - popupHeight / 2;

        const popup = window.open(
          redirectUrl.url,
          'jira-auth-popup',
          `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`,
        );

        if (popup) {
          const handleMessage = (e: MessageEvent) => {
            if (e.data?.type === 'jira-connected') {
              window.removeEventListener('message', handleMessage);
              clearInterval(closeInterval);
              toast.success('Jira connected successfully.');
              if (onUpdate) onUpdate();
            }
          };
          window.addEventListener('message', handleMessage);

          // Fallback: if popup closes without postMessage (user skipped), still refresh
          const closeInterval = setInterval(() => {
            if (popup.closed) {
              clearInterval(closeInterval);
              window.removeEventListener('message', handleMessage);
              if (onUpdate) onUpdate();
            }
          }, 1000);
        }
      } else {
        setErrorMessage('Failed to open Jira authentication window.');
      }
    } catch (error) {
      console.error('Error during Jira authentication:', error);
      setErrorMessage('An error occurred while connecting to Jira.');
    } finally {
      setIsConnecting(false);
    }
  };

  const overLimit =
    userProjects?.planLimit !== null &&
    userProjects?.planLimit !== undefined &&
    pendingSelected.size > userProjects.planLimit;

  const togglePending = (id: string) => {
    setPendingSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveProjects = async () => {
    setIsSavingProjects(true);
    try {
      await updateUserProjects(Array.from(pendingSelected), syncFromDate(syncPreset));
      toast.success('Projects updated.');
      setIsChangingProjects(false);
      loadProjects();
    } catch {
      toast.error('Failed to update projects.');
    } finally {
      setIsSavingProjects(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Top: status + primary actions */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <DiJira className="text-2xl opacity-70" />
            <div className="text-lg font-semibold">Jira</div>
            <span className={`badge ${isConnected ? 'badge-success' : 'badge-ghost'}`}>
              {isConnected ? 'Connected' : 'Not connected'}
            </span>
          </div>
          <div className="text-sm opacity-70">
            Connect Jira to sync projects, boards, issues and compute delivery stats.
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleJiraLogin}
            disabled={isConnecting || isSyncing}
            className={`btn btn-sm ${isConnected ? 'btn-outline' : 'btn-primary'}`}
          >
            {isConnecting ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Connecting…
              </>
            ) : isConnected ? (
              'Disconnect'
            ) : (
              'Connect'
            )}
          </button>

          <button
            onClick={handleJiraIncrementalSync}
            disabled={isConnecting || isSyncing || !isConnected}
            className="btn btn-sm btn-outline"
            title={!isConnected ? 'Connect Jira to enable sync' : 'Start incremental sync'}
          >
            {isSyncing ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Syncing…
              </>
            ) : (
              'Sync now'
            )}
          </button>

          {isConnected && (
            <button className="btn btn-sm btn-ghost" onClick={onUpdate} disabled={isConnecting || isSyncing}>
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {errorMessage && (
        <div className="alert alert-error">
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Guidance */}
      {!isConnected ? (
        <div className="alert bg-base-100 shadow-card">
          <span className="text-sm opacity-80">
            Click <b>Connect</b> to authorize Jira access. After that you can run <b>Sync now</b>.
          </span>
        </div>
      ) : (
        <div className="alert alert-info">
          <span className="text-sm">
            Tip: run <b>Sync now</b> after connecting (and anytime you want fresh stats).
          </span>
        </div>
      )}

      {/* Project selection (when connected) */}
      {isConnected && (
        <div className="rounded-box bg-base-200 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Synced projects</div>
            {!isChangingProjects && (
              <button
                className="btn btn-xs btn-ghost"
                onClick={() => {
                  setPendingSelected(new Set(userProjects?.selectedProjectIds ?? []));
                  setIsChangingProjects(true);
                }}
                disabled={isLoadingProjects}
              >
                Change
              </button>
            )}
          </div>

          {isLoadingProjects && <span className="loading loading-spinner loading-xs" />}

          {!isLoadingProjects && !isChangingProjects && userProjects && (
            <div className="flex flex-wrap gap-1">
              {userProjects.selectedProjectIds.length === 0 ? (
                <span className="text-sm opacity-50">No projects selected</span>
              ) : (
                userProjects.selectedProjectIds.map((id) => {
                  const p = userProjects.availableProjects.find((x) => x.id === id);
                  return (
                    <span key={id} className="badge badge-outline badge-sm">
                      {p ? p.name : id}
                    </span>
                  );
                })
              )}
            </div>
          )}

          {isChangingProjects && userProjects && (
            <>
              {userProjects.planLimit !== null && (
                <div className={`text-xs ${overLimit ? 'text-error' : 'opacity-60'}`}>
                  {overLimit
                    ? `Plan limit: max ${userProjects.planLimit}. Deselect ${pendingSelected.size - userProjects.planLimit}.`
                    : `${userProjects.plan} plan — up to ${userProjects.planLimit} project${userProjects.planLimit === 1 ? '' : 's'}`}
                </div>
              )}

              <div className="space-y-1 max-h-48 overflow-y-auto">
                {userProjects.availableProjects.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-base-300 rounded px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs checkbox-primary"
                      checked={pendingSelected.has(p.id)}
                      onChange={() => togglePending(p.id)}
                    />
                    <span className="text-sm">{p.name}</span>
                    <span className="text-xs opacity-40">{p.key}</span>
                  </label>
                ))}
              </div>

              <div className="space-y-1 pt-1">
                <label htmlFor="jira-integration-sync-from" className="text-xs font-medium opacity-70">
                  Sync history from
                </label>
                <select
                  id="jira-integration-sync-from"
                  className="select select-bordered select-xs w-full"
                  value={syncPreset}
                  onChange={(e) => setSyncPreset(Number(e.target.value))}
                >
                  {SYNC_PRESETS.map((p) => (
                    <option key={p.months} value={p.months}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs opacity-40">Changelog data before this date won&apos;t be imported.</p>
              </div>

              <div className="flex gap-2 justify-end pt-1">
                <button
                  className="btn btn-xs btn-ghost"
                  onClick={() => setIsChangingProjects(false)}
                  disabled={isSavingProjects}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-xs btn-primary"
                  onClick={handleSaveProjects}
                  disabled={isSavingProjects || overLimit}
                >
                  {isSavingProjects ? <span className="loading loading-spinner loading-xs" /> : null}
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Permissions */}
      <div className="collapse collapse-arrow bg-base-200">
        <input type="checkbox" />
        <div className="collapse-title font-medium">Permissions & OAuth details</div>
        <div className="collapse-content text-sm opacity-80 space-y-2">
          <div className="font-medium">MFlow may request access to:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>View and manage Jira projects</li>
            <li>View and manage Jira issues</li>
            <li>View and manage Jira boards and sprints</li>
            <li>View and manage filters and comments</li>
          </ul>

          <a
            className="link link-primary"
            rel="noreferrer"
            href="https://developer.atlassian.com/cloud/jira/platform/oauth-2-3lo-apps/"
            target="_blank"
          >
            How Jira OAuth works →
          </a>
        </div>
      </div>
    </div>
  );
};

export default JiraIntegration;
